import React, { useRef, useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  CCard,
  CCardHeader,
  CCardBody,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CFormInput,
  CFormCheck,
  CButton,
  CSpinner,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CPagination,
  CPaginationItem,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CInputGroup,
  CInputGroupText,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilMap,
  cilChartLine,
  cilSpeedometer,
  cilBatteryFull,
  cibLaravel,
  cilLayers,
  cilTrash,
  cilReload,
  cilSettings,
  cilVideo,
  cilClock,
  cilCalendar,
  cilSave,
  cilInfo,
  cilX,
  cilFullscreen,
  cilFullscreenExit,
  cilMobile,
  cilBell,
  cilPlus,
  cilPencil,
  cilCloudUpload,
  cilSearch,
  cilLocationPin,
} from "@coreui/icons";
import { ResponsiveLine } from "@nivo/line";
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { YMaps, Map as YandexMap, Polyline, Placemark, ZoomControl, TypeSelector } from "@pbe/react-yandex-maps";
import GPSMap from "../../../components/GPSMap/GPSMap";
import DeviceAlertsTab from "./DeviceAlertsTab";
import DowntimesTab from "./DowntimesTab";
import SafeTooltip from "../../../components/SafeTooltip/SafeTooltip";

// --- Встроенные стили для исправления анимации и унификации ---
const customStyles = `
  .live-dot-fixed {
    display: inline-block;
    width: 8px;
    height: 8px;
    background-color: #e55353;
    border-radius: 50%;
    margin-right: 7px;
    flex-shrink: 0;
    align-self: center;
    animation: live-perfect-pulse 1.5s cubic-bezier(0.2, 0.8, 0.2, 1) infinite;
  }
  @keyframes live-perfect-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(229, 83, 83, 0.85);
    }
    100% {
      box-shadow: 0 0px 0 8px rgba(229, 83, 83, 0);
    }
  }
  .settings-section {
    background-color: #ffffff;
    border: 1px solid #e4e7ea;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  }
  .site-list-item {
    transition: background-color 0.15s ease;
  }
  .site-list-item:hover {
    background-color: #f8f9fa;
  }
`;

// --- Вспомогательные компоненты для единого стиля ---
const SectionHeader = ({ icon, title, iconClass = "text-primary", tooltip, rightContent }) => (
  <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2 flex-wrap gap-2">
    <div className="d-flex align-items-center">
      <div className="bg-light p-2 rounded me-3 d-flex align-items-center justify-content-center border">
        <CIcon icon={icon} className="text-dark" size="lg" />
      </div>
      <h5 className="mb-0 fw-bold text-dark">{title}</h5>
      {tooltip && (
        <SafeTooltip content={tooltip}>
          <CIcon icon={cilInfo} className="text-muted ms-2 mt-1" style={{ cursor: "help", opacity: 0.6, width: "18px", height: "18px" }} />
        </SafeTooltip>
      )}
    </div>
    {rightContent && <div>{rightContent}</div>}
  </div>
);

const FormLabel = ({ children, className = "" }) => (
  <label className={`fw-bold text-muted small text-uppercase mb-1 d-block ${className}`} style={{ letterSpacing: "0.5px" }}>{children}</label>
);

const CameraTabs = ({
  activeTab,
  setActiveTab,
  schedule,
  handleScheduleChange,
  handleWorkDayChange,
  savingSchedule,
  handleSaveSchedule,
  shifts,
  newShift,
  setNewShift,
  handleAddShift,
  handleDeleteShift,
  breaks,
  newBreak,
  setNewBreak,
  handleAddBreak,
  handleDeleteBreak,
  exceptions,
  newException,
  setNewException,
  handleAddException,
  handleDeleteException,
  materials,
  newMaterial,
  setNewMaterial,
  handleAddMaterial,
  handleDeleteMaterial,
  trajectoryData,
  currentAbsoluteTime,
  telemetryData,
  analyticsData,
  handleRecalculateTwin,
  twinRecalculating,
  twinPage,
  setTwinPage,
  totalTwinPages,
  totalTwinItems,
  currentTwinData,
  getPaginationItems,
  setViewerUrl,
  setIsModelLoading,
  setViewerModalVisible,
  cameraId,
  liveData,
  isStaff,
  isSuperuser,
  handleRealtimeToggle,
  handleTwinPhotosToggle,
  setLiveImageModalVisible,
  onClose,
  onVideoJump,
  variant = "excavator"
}) => {
  // Вариант техники: самосвал, мусоровоз или экскаватор
  const isTruck = variant === "truck";
  const isGarbage = variant === "garbage";
  const { t } = useTranslation();
  const twinScrollRef = useRef(null);
  const containerRef = useRef(null);
  const weekDayKeys = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

  const [isExpanded, setIsExpanded] = useState(false);
  const [fullScreenMapVisible, setFullScreenMapVisible] = useState(false);

  // --- СТЕЙТЫ ДЛЯ СПРАВОЧНИКА АДРЕСОВ КП (МУСОРОВОЗЫ) ---
  const [garbageSites, setGarbageSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [siteSearchQuery, setSiteSearchQuery] = useState("");

  // Модалка добавления / редактирования площадки
  const [siteFormModal, setSiteFormModal] = useState({
    visible: false,
    mode: "add", // 'add' | 'edit'
    siteId: null,
    address: "",
    external_id: "",
    latitude: "",
    longitude: "",
    saving: false,
  });

  // Модалка импорта Excel
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [replaceOnImport, setReplaceOnImport] = useState(false);
  const [isDraggingExcel, setIsDraggingExcel] = useState(false);
  const excelInputRef = useRef(null);

  // Модалка подтверждения удаления (одной площадки или всех)
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    visible: false,
    type: "single", // 'single' | 'all'
    site: null,
    deleting: false,
  });

  const fetchGarbageSites = async () => {
    if (!isGarbage || !cameraId) return;
    setLoadingSites(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/devices/${cameraId}/garbage-sites/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGarbageSites(res.data || []);
    } catch (e) {
      console.error("Ошибка загрузки списка адресов КП", e);
    } finally {
      setLoadingSites(false);
    }
  };

  useEffect(() => {
    if (isGarbage && activeTab === "addresses") {
      fetchGarbageSites();
    }
  }, [isGarbage, activeTab, cameraId]);

  const filteredGarbageSites = useMemo(() => {
    const q = siteSearchQuery.trim().toLowerCase();
    if (!q) return garbageSites;
    return garbageSites.filter(
      (s) =>
        (s.address && s.address.toLowerCase().includes(q)) ||
        (s.external_id && String(s.external_id).toLowerCase().includes(q)) ||
        `${s.latitude}, ${s.longitude}`.includes(q)
    );
  }, [garbageSites, siteSearchQuery]);

  const openAddSiteModal = () => {
    setSiteFormModal({
      visible: true,
      mode: "add",
      siteId: null,
      address: "",
      external_id: "",
      latitude: "",
      longitude: "",
      saving: false,
    });
  };

  const openEditSiteModal = (site) => {
    setSiteFormModal({
      visible: true,
      mode: "edit",
      siteId: site.id,
      address: site.address || "",
      external_id: site.external_id || "",
      latitude: site.latitude ?? "",
      longitude: site.longitude ?? "",
      saving: false,
    });
  };

  const handleSaveSiteModal = async () => {
    const { mode, siteId, address, external_id, latitude, longitude } = siteFormModal;
    const parsedLat = parseFloat(String(latitude).replace(",", "."));
    const parsedLon = parseFloat(String(longitude).replace(",", "."));

    if (!address.trim() || isNaN(parsedLat) || isNaN(parsedLon)) {
      alert("Пожалуйста, укажите адрес и корректные координаты (широту и долготу).");
      return;
    }

    setSiteFormModal((prev) => ({ ...prev, saving: true }));
    try {
      const token = localStorage.getItem("token");
      const payload = {
        address: address.trim(),
        external_id: String(external_id || "").trim(),
        latitude: parsedLat,
        longitude: parsedLon,
      };

      if (mode === "add") {
        const res = await axios.post(`/api/devices/${cameraId}/garbage-sites/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGarbageSites((prev) => [res.data, ...prev]);
      } else {
        const res = await axios.patch(
          `/api/devices/${cameraId}/garbage-sites/${siteId}/`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setGarbageSites((prev) => prev.map((s) => (s.id === siteId ? res.data : s)));
      }
      setSiteFormModal((prev) => ({ ...prev, visible: false, saving: false }));
    } catch (e) {
      alert("Ошибка при сохранении площадки. Проверьте правильность введенных данных.");
      setSiteFormModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleConfirmDelete = async () => {
    setDeleteConfirmModal((prev) => ({ ...prev, deleting: true }));
    try {
      const token = localStorage.getItem("token");
      if (deleteConfirmModal.type === "all") {
        await axios.delete(`/api/devices/${cameraId}/garbage-sites/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGarbageSites([]);
      } else if (deleteConfirmModal.site) {
        const siteId = deleteConfirmModal.site.id;
        await axios.delete(`/api/devices/${cameraId}/garbage-sites/${siteId}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGarbageSites((prev) => prev.filter((s) => s.id !== siteId));
      }
      setDeleteConfirmModal({ visible: false, type: "single", site: null, deleting: false });
    } catch (e) {
      console.error(e);
      alert("Не удалось выполнить удаление");
      setDeleteConfirmModal((prev) => ({ ...prev, deleting: false }));
    }
  };

  const handleExcelFileUpload = async (file) => {
    if (!file) return;
    setUploadingExcel(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("replace", replaceOnImport);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `/api/devices/${cameraId}/garbage-sites/import-excel/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setGarbageSites(res.data.sites || []);
      setImportModalVisible(false);
    } catch (e) {
      alert(e.response?.data?.error || "Ошибка импорта файла Excel");
    } finally {
      setUploadingExcel(false);
      if (excelInputRef.current) excelInputRef.current.value = "";
    }
  };

  // Скролл вверх при переключении страниц двойника
  useEffect(() => {
    if (twinScrollRef.current) {
      twinScrollRef.current.scrollTop = 0;
    }
  }, [twinPage]);

  // Эффект для динамического изменения ширины и скруглений родительского Offcanvas
  useEffect(() => {
    const applyOffcanvasStyles = () => {
      if (containerRef.current) {
        const offcanvas = containerRef.current.closest('.offcanvas');
        if (offcanvas) {
          offcanvas.style.transition = 'transform 0.3s ease-in-out, width 0.3s ease-in-out';
          if (window.innerWidth >= 992) {
            offcanvas.style.width = isExpanded ? '60vw' : '30vw';
            offcanvas.style.borderTopLeftRadius = '16px';
            offcanvas.style.borderBottomLeftRadius = '16px';
          } else {
            offcanvas.style.width = '100vw';
            offcanvas.style.borderTopLeftRadius = '0px';
            offcanvas.style.borderBottomLeftRadius = '0px';
            setIsExpanded(false);
          }
        }
      }
    };

    applyOffcanvasStyles();
    window.addEventListener('resize', applyOffcanvasStyles);
    return () => window.removeEventListener('resize', applyOffcanvasStyles);
  }, [isExpanded]);

  // --- ЛОГИКА КАРТЫ ДЛЯ МУСОРОВОЗОВ ---
  const sites = analyticsData?.sites_summary || [];
  const routeCoords = useMemo(() => {
    if (!trajectoryData) return [];
    return trajectoryData.filter(p => p.lat > 0 && p.lon > 0).map(p => [p.lat, p.lon]);
  }, [trajectoryData]);

  const mapCenter = useMemo(() => {
    const validSite = sites.find(s => s.latitude > 0 && s.longitude > 0);
    if (validSite) return [validSite.latitude, validSite.longitude];
    if (routeCoords.length > 0) return routeCoords[0];
    return [55.751574, 37.573856];
  }, [sites, routeCoords]);

  const getSiteColor = (site) => {
    if (site.has_unemptied_containers) return "#e55353";
    if (site.has_overflow || site.has_bulky_waste || site.has_container_defect) return "#e65100";
    return "#2eb85c";
  };

  const renderGarbageMap = (isFullScreen = false) => (
    <YMaps query={{ lang: "ru_RU" }}>
      <YandexMap
        defaultState={{ center: mapCenter, zoom: 12 }}
        width="100%"
        height="100%"
      >
        {routeCoords.length > 1 && (
          <Polyline
            geometry={routeCoords}
            options={{
              balloonCloseButton: false,
              strokeColor: "#0d6efd",
              strokeWidth: 4,
              strokeOpacity: 0.7,
            }}
          />
        )}
        {sites.filter(s => s.latitude > 0 && s.longitude > 0).map((site, index) => (
          <Placemark
            key={site.id}
            geometry={[site.latitude, site.longitude]}
            properties={{
              hintContent: `<strong>КП #${index + 1}</strong><br/>Контейнеров: ${site.containers_count}<br/>Кликните для просмотра видео`,
            }}
            options={{
              preset: "islands#circleDotIcon",
              iconColor: getSiteColor(site),
            }}
            onClick={() => {
              if (onVideoJump) onVideoJump(new Date(site.start).getTime());
              if (isFullScreen) setFullScreenMapVisible(false);
              onClose();
            }}
          />
        ))}
        <ZoomControl options={{ float: "right" }} />
        <TypeSelector options={{ float: "right" }} />
      </YandexMap>
    </YMaps>
  );

  // --- Содержимое для тултипов инфо-кнопок ---
  const videoParamsTooltip = (
    <div className="small text-start" style={{ maxWidth: '320px', lineHeight: '1.4' }}>
      <strong>Базовая ширина:</strong> Если у вас старое устройство (координаты в пикселях), укажите ширину, в которой нейросеть детектировала объекты (например, 640). Новые устройства (координаты 0-1) не зависят от этой настройки.<br /><br />
      <strong>Смещение от МСК:</strong> Например, для UTC+5 введите 2.
    </div>
  );

  const analyticsTooltip = (
    <div className="small text-start" style={{ maxWidth: '320px', lineHeight: '1.4' }}>
      <strong>Макс. время цикла:</strong> Отсекает аномально долгие циклы при расчете средних значений.<br /><br />
      <strong>Порог ожидания:</strong> Отправляет уведомление, если техника простаивает с пустым ковшом дольше этого времени.<br /><br />
      <strong>Мин. ковшей (сплит):</strong> Склеит соседние заезды, если в них меньше ковшей.<br /><br />
      <strong>Макс. ковшей:</strong> Предотвратит склейку, если итог превысит этот лимит.<br /><br />
      <strong>Мин. вр. между машинами:</strong> Запретит одной метке отмечаться чаще заданного времени.
    </div>
  );

  const systemTooltip = (
    <div className="small text-start" style={{ maxWidth: '320px', lineHeight: '1.4' }}>
      <strong>Live Трансляция:</strong> Планшет будет тратить мобильный интернет на отправку фото. Включайте только для отладки.<br /><br />
      <strong>Скриншоты Двойника:</strong> Если отключить, объемы продолжат считаться, но фото-замеров в отчетах не будет.
    </div>
  );

  return (
    <div className="d-flex flex-column h-100 bg-light" ref={containerRef}>
      <style>{customStyles}</style>

      {/* --- ШАПКА ПАНЕЛИ --- */}
      <div className="bg-white px-3 pt-3 border-bottom shadow-sm">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0 text-dark fw-bold">Панель управления</h5>
          <div className="d-flex gap-2">
            <CButton
              color="light"
              variant="ghost"
              className="d-none d-lg-flex align-items-center justify-content-center p-1 text-secondary"
              onClick={() => setIsExpanded(!isExpanded)}
              title={isExpanded ? "Сузить панель" : "Расширить панель"}
            >
              <CIcon icon={isExpanded ? cilFullscreenExit : cilFullscreen} size="lg" />
            </CButton>
            <CButton
              color="light"
              variant="ghost"
              className="align-items-center justify-content-center p-1 text-dark"
              onClick={onClose}
              title="Закрыть"
            >
              <CIcon icon={cilX} size="xl" />
            </CButton>
          </div>
        </div>

        <CNav
          variant="pills"
          className="modern-pills d-grid gap-2 pb-3"
          style={{ gridTemplateColumns: "1fr 1fr" }}
        >
          <CNavItem>
            <CNavLink className="d-flex align-items-center justify-content-center" active={activeTab === "settings"} onClick={() => setActiveTab("settings")} style={{ cursor: "pointer" }}>
              <CIcon icon={cilSettings} className="me-2" /> {t("tabs.settings")}
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink className="d-flex align-items-center justify-content-center" active={activeTab === "materials"} onClick={() => setActiveTab("materials")} style={{ cursor: "pointer" }}>
              <CIcon icon={cilLayers} className="me-2" /> {t("tabs.materials")}
            </CNavLink>
          </CNavItem>

          {/* Вкладка адресов для мусоровозов */}
          {isGarbage && (
            <CNavItem>
              <CNavLink
                className="d-flex align-items-center justify-content-center"
                active={activeTab === "addresses"}
                onClick={() => setActiveTab("addresses")}
                style={{ cursor: "pointer" }}
              >
                <CIcon icon={cilMap} className="me-2" /> Список адресов
              </CNavLink>
            </CNavItem>
          )}

          {/* У самосвала карта на основной странице */}
          {!isTruck && (
            <CNavItem>
              <CNavLink
                className="d-flex align-items-center justify-content-center"
                active={activeTab === "map"}
                onClick={() => setActiveTab("map")}
                style={{ cursor: "pointer" }}
              >
                <CIcon icon={cilMap} className="me-2" /> {t("tabs.map")}
              </CNavLink>
            </CNavItem>
          )}
          <CNavItem>
            <CNavLink className="d-flex align-items-center justify-content-center" active={activeTab === "telemetry"} onClick={() => setActiveTab("telemetry")} style={{ cursor: "pointer" }}>
              <CIcon icon={cilChartLine} className="me-2" /> {t("tabs.telemetry")}
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink className="d-flex align-items-center justify-content-center" active={activeTab === "fuel"} onClick={() => setActiveTab("fuel")} style={{ cursor: "pointer" }}>
              <CIcon icon={cilSpeedometer} className="me-2" /> Топливо
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink className="d-flex align-items-center justify-content-center" active={activeTab === "alerts"} onClick={() => setActiveTab("alerts")} style={{ cursor: "pointer" }}>
              <CIcon icon={cilBell} className="me-2" /> {t("tabs.alerts", "Уведомления")}
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink className="d-flex align-items-center justify-content-center" active={activeTab === "downtimes"} onClick={() => setActiveTab("downtimes")} style={{ cursor: "pointer" }}>
              <CIcon icon={cilClock} className="me-2" /> Простои
            </CNavLink>
          </CNavItem>
          {analyticsData && analyticsData.use_digital_twin && (
            <CNavItem>
              <CNavLink className="d-flex align-items-center justify-content-center" active={activeTab === "twin"} onClick={() => setActiveTab("twin")} style={{ cursor: "pointer" }}>
                <CIcon icon={cibLaravel} className="me-2" /> {t("tabs.twin")}
              </CNavLink>
            </CNavItem>
          )}
          <CNavItem>
            <CNavLink className="d-flex align-items-center justify-content-center" active={activeTab === "live"} onClick={() => setActiveTab("live")} style={{ cursor: "pointer" }}>
              <span className="live-dot-fixed" /> Live
            </CNavLink>
          </CNavItem>
        </CNav>
      </div>

      {/* --- ТЕЛО ТАБОВ --- */}
      <div className="p-3 overflow-auto" style={{ flexGrow: 1 }}>
        <CTabContent>
          {/* ==========================================
              ВКЛАДКА 1: НАСТРОЙКИ
          ========================================== */}
          <CTabPane visible={activeTab === "settings"}>
            <div className="settings-section">
              <SectionHeader icon={cilVideo} title={t("camera_settings.video_params")} tooltip={videoParamsTooltip} />
              <CRow className="g-3">
                {!isTruck && (
                  <CCol md={6}>
                    <div style={{ minHeight: '48px' }}>
                      <FormLabel className="mb-0">{t("camera_settings.base_width")}</FormLabel>
                    </div>
                    <CFormInput
                      type="number"
                      size="sm"
                      name="frame_width"
                      value={schedule.frame_width}
                      onChange={handleScheduleChange}
                      placeholder={t("camera_settings.base_width_ph")}
                    />
                    <div className="form-text text-muted small mt-1">
                      {t("camera_settings.base_width_hint")}
                    </div>
                  </CCol>
                )}

                <CCol md={6}>
                  <FormLabel>{t("camera.timezone_offset")}</FormLabel>
                  <CFormInput
                    type="number"
                    size="sm"
                    name="timezone_offset"
                    value={schedule.timezone_offset}
                    onChange={handleScheduleChange}
                  />
                </CCol>
              </CRow>
            </div>

            {/* Блок: Аналитика рейсов (самосвал) */}
            {isTruck && (
              <div className="settings-section">
                <SectionHeader icon={cilChartLine} title="Аналитика рейсов" />
                <CRow className="g-3">
                  <CCol xs={12} md={6}>
                    <FormLabel title="Геометрический объем кузова. Идет в объем каждого рейса">
                      Объем кузова (м³)
                    </FormLabel>
                    <CFormInput
                      type="number"
                      step="0.1"
                      size="sm"
                      name="bucket_volume"
                      value={schedule.bucket_volume}
                      onChange={handleScheduleChange}
                    />
                    <div className="form-text text-muted small mt-1">
                      Из него считаются объем, масса и грузооборот рейсов.
                    </div>
                  </CCol>
                  <CCol xs={12} md={6}>
                    <FormLabel title="Простои длиннее попадают в журнал. Стоянка пустым дольше порога обрывает начатый рейс">
                      Порог простоя (сек)
                    </FormLabel>
                    <CFormInput
                      type="number"
                      size="sm"
                      name="downtime_threshold_sec"
                      value={schedule.downtime_threshold_sec}
                      onChange={handleScheduleChange}
                    />
                    <div className="form-text text-muted small mt-1">
                      Порог для журнала длинных простоев.
                    </div>
                  </CCol>
                </CRow>
              </div>
            )}

            {/* Блок: Аналитика (экскаватор) */}
            {!isTruck && (
              <div className="settings-section">
                <SectionHeader icon={cilChartLine} title={t("camera.settings_analytics")} tooltip={analyticsTooltip} />
                <CRow className="g-3">
                  <CCol xs={12} md={6}>
                    <FormLabel>{t("camera.max_cycle_time")}</FormLabel>
                    <CFormInput
                      type="number"
                      size="sm"
                      name="max_cycle_time_sec"
                      value={schedule.max_cycle_time_sec}
                      onChange={handleScheduleChange}
                    />
                  </CCol>

                  <CCol xs={12} md={6}>
                    <FormLabel>Порог ожидания самосвала (сек)</FormLabel>
                    <CFormInput
                      type="number"
                      size="sm"
                      name="idle_truck_threshold_sec"
                      value={schedule.idle_truck_threshold_sec}
                      onChange={handleScheduleChange}
                    />
                  </CCol>
                </CRow>

                <div className="mt-4 mb-2 fw-bold text-dark border-bottom pb-2">Объединение заездов</div>
                <CRow className="g-3 mt-1">
                  <CCol xs={10} md={4}>
                    <FormLabel>Мин. ковшей (сплит)</FormLabel>
                    <CFormInput
                      type="number"
                      size="sm"
                      name="min_cycles_to_prevent_split"
                      value={schedule.min_cycles_to_prevent_split}
                      onChange={handleScheduleChange}
                    />
                  </CCol>
                  <CCol xs={10} md={4}>
                    <FormLabel>Макс. ковшей в заезде</FormLabel>
                    <CFormInput
                      type="number"
                      size="sm"
                      name="max_cycles_per_session"
                      value={schedule.max_cycles_per_session}
                      onChange={handleScheduleChange}
                    />
                  </CCol>
                  <CCol xs={10} md={4}>
                    <FormLabel>Мин. вр. между машинами</FormLabel>
                    <CFormInput
                      type="number"
                      size="sm"
                      name="min_truck_cooldown_sec"
                      value={schedule.min_truck_cooldown_sec}
                      onChange={handleScheduleChange}
                    />
                  </CCol>
                </CRow>
              </div>
            )}

            {/* Блок: График работы (Дни и Смены) */}
            <div className="settings-section">
              <SectionHeader icon={cilClock} title={t("camera.schedule_settings")} />
              <div className="mb-4">
                <FormLabel>{t("camera.workdays")}</FormLabel>
                <div className="d-flex flex-wrap gap-2 mt-1">
                  {weekDayKeys.map((dayKey, i) => (
                    <CFormCheck
                      button={{ color: 'primary', variant: 'outline', size: 'sm' }}
                      id={`day-${i}`}
                      key={i}
                      label={t(`camera.days.${dayKey}`)}
                      checked={(schedule.work_days >> i) & 1}
                      onChange={() => handleWorkDayChange(i)}
                    />
                  ))}
                </div>
              </div>

              <CFormCheck
                id="has_multiple_shifts"
                name="has_multiple_shifts"
                label={<span className="fw-bold text-dark">{t("camera.multiple_shifts")}</span>}
                checked={schedule.has_multiple_shifts}
                onChange={handleScheduleChange}
                className="mb-3"
              />

              {schedule.has_multiple_shifts ? (
                <>
                  {shifts.length > 0 ? (
                    <div className="settings-table-wrapper mb-3 border rounded">
                      <CTable small hover align="middle" className="mb-0">
                        <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                          <CTableRow>
                            <CTableHeaderCell>{t("camera_tabs_extra.name")}</CTableHeaderCell>
                            <CTableHeaderCell className="text-center">{t("camera_tabs_extra.start")}</CTableHeaderCell>
                            <CTableHeaderCell className="text-center">{t("camera_tabs_extra.end")}</CTableHeaderCell>
                            <CTableHeaderCell />
                          </CTableRow>
                        </CTableHead>
                        <CTableBody className="bg-white">
                          {shifts.map((s) => (
                            <CTableRow key={s.id}>
                              <CTableDataCell className="fw-semibold text-dark">{s.name}</CTableDataCell>
                              <CTableDataCell className="text-center">{s.start_time.substring(0, 5)}</CTableDataCell>
                              <CTableDataCell className="text-center">{s.end_time.substring(0, 5)}</CTableDataCell>
                              <CTableDataCell className="text-end">
                                <CButton
                                  size="sm"
                                  color="danger"
                                  variant="ghost"
                                  onClick={() => handleDeleteShift(s.id)}
                                >
                                  <CIcon icon={cilTrash} />
                                </CButton>
                              </CTableDataCell>
                            </CTableRow>
                          ))}
                        </CTableBody>
                      </CTable>
                    </div>
                  ) : (
                    <div className="text-center text-muted small p-4 bg-white rounded border border-dashed mb-3">
                      Смены не добавлены. Заполните форму ниже.
                    </div>
                  )}

                  <div className="bg-light p-3 rounded border">
                    <CRow className="g-2 align-items-end">
                      <CCol xs={12} md={4}>
                        <FormLabel>{t("camera_tabs_extra.name")}</FormLabel>
                        <CFormInput
                          size="sm"
                          type="text"
                          value={newShift.name}
                          onChange={(e) =>
                            setNewShift((p) => ({ ...p, name: e.target.value }))
                          }
                        />
                      </CCol>
                      <CCol xs={6} md={3}>
                        <FormLabel>{t("camera_tabs_extra.start")}</FormLabel>
                        <CFormInput
                          size="sm"
                          type="time"
                          value={newShift.start_time}
                          onChange={(e) =>
                            setNewShift((p) => ({
                              ...p,
                              start_time: e.target.value,
                            }))
                          }
                        />
                      </CCol>
                      <CCol xs={6} md={3}>
                        <FormLabel>{t("camera_tabs_extra.end")}</FormLabel>
                        <CFormInput
                          size="sm"
                          type="time"
                          value={newShift.end_time}
                          onChange={(e) =>
                            setNewShift((p) => ({ ...p, end_time: e.target.value }))
                          }
                        />
                      </CCol>
                      <CCol xs={12} md={2}>
                        <CButton
                          color="primary"
                          className="w-100 fw-bold shadow-sm text-white"
                          onClick={handleAddShift}
                        >
                          Добавить
                        </CButton>
                      </CCol>
                    </CRow>
                  </div>
                </>
              ) : (
                <div className="bg-light p-3 rounded border">
                  <CRow className="g-3">
                    <CCol xs={6}>
                      <FormLabel>{t("camera.shift_start")}</FormLabel>
                      <CFormInput
                        size="sm"
                        type="time"
                        name="shift_start"
                        value={schedule.shift_start}
                        onChange={handleScheduleChange}
                      />
                    </CCol>
                    <CCol xs={6}>
                      <FormLabel>{t("camera.shift_end")}</FormLabel>
                      <CFormInput
                        size="sm"
                        type="time"
                        name="shift_end"
                        value={schedule.shift_end}
                        onChange={handleScheduleChange}
                      />
                    </CCol>
                  </CRow>
                </div>
              )}
            </div>

            {/* Блок: Перерывы */}
            <div className="settings-section">
              <SectionHeader icon={cilClock} title={t("camera.breaks")} />

              <CFormCheck
                id="include_breaks_in_stats"
                name="include_breaks_in_stats"
                label={<span className="fw-bold">Учитывать выработку в перерывы в статистике</span>}
                checked={schedule.include_breaks_in_stats}
                onChange={handleScheduleChange}
                className="mb-3"
              />

              {breaks.length > 0 ? (
                <div className="settings-table-wrapper mb-3 border rounded">
                  <CTable small hover align="middle" className="mb-0">
                    <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <CTableRow>
                        <CTableHeaderCell className="text-center">{t("camera_tabs_extra.start")}</CTableHeaderCell>
                        <CTableHeaderCell className="text-center">{t("camera_tabs_extra.end")}</CTableHeaderCell>
                        <CTableHeaderCell>{t("camera_tabs_extra.desc")}</CTableHeaderCell>
                        <CTableHeaderCell />
                      </CTableRow>
                    </CTableHead>
                    <CTableBody className="bg-white">
                      {breaks.map((b) => (
                        <CTableRow key={b.id}>
                          <CTableDataCell className="fw-semibold text-dark text-center">{b.start_time.substring(0, 5)}</CTableDataCell>
                          <CTableDataCell className="fw-semibold text-dark text-center">{b.end_time.substring(0, 5)}</CTableDataCell>
                          <CTableDataCell className="text-muted small">{b.description}</CTableDataCell>
                          <CTableDataCell className="text-end">
                            <CButton
                              size="sm"
                              color="danger"
                              variant="ghost"
                              onClick={() => handleDeleteBreak(b.id)}
                            >
                              <CIcon icon={cilTrash} />
                            </CButton>
                          </CTableDataCell>
                        </CTableRow>
                      ))}
                    </CTableBody>
                  </CTable>
                </div>
              ) : (
                <div className="text-center text-muted small p-4 bg-white rounded border border-dashed mb-3">
                  Перерывы в работе не заданы.
                </div>
              )}

              <div className="bg-light p-3 rounded border">
                <CRow className="g-2 align-items-end">
                  <CCol xs={6} md={3}>
                    <FormLabel>{t("camera_tabs_extra.start")}</FormLabel>
                    <CFormInput
                      size="sm"
                      type="time"
                      value={newBreak.start_time}
                      onChange={(e) => setNewBreak((p) => ({ ...p, start_time: e.target.value }))}
                    />
                  </CCol>
                  <CCol xs={6} md={3}>
                    <FormLabel>{t("camera_tabs_extra.end")}</FormLabel>
                    <CFormInput
                      size="sm"
                      type="time"
                      value={newBreak.end_time}
                      onChange={(e) => setNewBreak((p) => ({ ...p, end_time: e.target.value }))}
                    />
                  </CCol>
                  <CCol xs={12} md={4}>
                    <FormLabel>{t("camera_tabs_extra.desc")}</FormLabel>
                    <CFormInput
                      size="sm"
                      type="text"
                      value={newBreak.description}
                      onChange={(e) => setNewBreak((p) => ({ ...p, description: e.target.value }))}
                    />
                  </CCol>
                  <CCol xs={12} md={2}>
                    <CButton
                      color="primary"
                      className="w-100 fw-bold shadow-sm text-white"
                      onClick={handleAddBreak}
                    >
                      Добавить
                    </CButton>
                  </CCol>
                </CRow>
              </div>
            </div>

            {/* Блок: Исключения */}
            <div className="settings-section">
              <SectionHeader icon={cilCalendar} title={t("camera.exceptions")} />

              {exceptions.length > 0 ? (
                <div className="settings-table-wrapper mb-3 border rounded">
                  <CTable small hover align="middle" className="mb-0">
                    <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <CTableRow>
                        <CTableHeaderCell>Период</CTableHeaderCell>
                        <CTableHeaderCell>Тип</CTableHeaderCell>
                        <CTableHeaderCell>Описание</CTableHeaderCell>
                        <CTableHeaderCell />
                      </CTableRow>
                    </CTableHead>
                    <CTableBody className="bg-white">
                      {exceptions.map((ex) => (
                        <CTableRow key={ex.id}>
                          <CTableDataCell className="fw-semibold text-dark" style={{ fontSize: "0.85em" }}>
                            {dayjs(ex.date).format("DD.MM.YY")}{" "}
                            {ex.end_date && ex.end_date !== ex.date
                              ? `- ${dayjs(ex.end_date).format("DD.MM.YY")}`
                              : ""}
                          </CTableDataCell>
                          <CTableDataCell style={{ fontSize: "0.85em" }}>
                            {ex.is_maintenance ? (
                              <span className="text-warning fw-bold">ТО / Ремонт</span>
                            ) : ex.is_working ? (
                              <span className="text-success fw-bold">Спец. смена</span>
                            ) : (
                              <span className="text-danger fw-bold">Выходной</span>
                            )}
                          </CTableDataCell>
                          <CTableDataCell className="text-muted small">
                            {ex.description || "-"}
                          </CTableDataCell>
                          <CTableDataCell className="text-end">
                            <CButton
                              size="sm"
                              color="danger"
                              variant="ghost"
                              onClick={() => handleDeleteException(ex.id)}
                            >
                              <CIcon icon={cilTrash} />
                            </CButton>
                          </CTableDataCell>
                        </CTableRow>
                      ))}
                    </CTableBody>
                  </CTable>
                </div>
              ) : (
                <div className="text-center text-muted small p-4 bg-white rounded border border-dashed mb-3">
                  Исключения (праздники/ТО) не заданы.
                </div>
              )}

              <div className="bg-light p-3 rounded border">
                <CRow className="g-3 align-items-end">
                  <CCol xs={12} md={6}>
                    <FormLabel>Выбор дат</FormLabel>
                    <DatePicker
                      selected={newException.startDate}
                      onChange={(dates) => {
                        const [start, end] = dates;
                        setNewException((p) => ({ ...p, startDate: start, endDate: end }));
                      }}
                      startDate={newException.startDate}
                      endDate={newException.endDate}
                      selectsRange
                      className="form-control form-control-sm w-100"
                      dateFormat="dd.MM.yyyy"
                      locale={t("language") === "en" ? "en-US" : "ru"}
                    />
                  </CCol>
                  <CCol xs={12} md={6}>
                    <FormLabel>Название / Описание</FormLabel>
                    <CFormInput
                      size="sm"
                      type="text"
                      placeholder="Например: Праздник"
                      value={newException.description}
                      onChange={(e) => setNewException((p) => ({ ...p, description: e.target.value }))}
                    />
                  </CCol>

                  <CCol xs={6}>
                    <CFormCheck
                      id="is_working_new"
                      label={<span className="fw-semibold text-success">Рабочий день</span>}
                      checked={newException.is_working}
                      onChange={(e) => setNewException((p) => ({ ...p, is_working: e.target.checked }))}
                    />
                  </CCol>
                  <CCol xs={6}>
                    <CFormCheck
                      id="is_maintenance_new"
                      label={<span className="fw-semibold text-warning">ТО / Ремонт</span>}
                      checked={newException.is_maintenance}
                      onChange={(e) => setNewException((p) => ({ ...p, is_maintenance: e.target.checked }))}
                    />
                  </CCol>

                  {newException.is_working && !newException.is_maintenance && (
                    <>
                      <CCol xs={6}>
                        <FormLabel>{t("camera_tabs_extra.start")}</FormLabel>
                        <CFormInput
                          size="sm"
                          type="time"
                          value={newException.custom_shift_start}
                          onChange={(e) => setNewException((p) => ({ ...p, custom_shift_start: e.target.value }))}
                        />
                      </CCol>
                      <CCol xs={6}>
                        <FormLabel>{t("camera_tabs_extra.end")}</FormLabel>
                        <CFormInput
                          size="sm"
                          type="time"
                          value={newException.custom_shift_end}
                          onChange={(e) => setNewException((p) => ({ ...p, custom_shift_end: e.target.value }))}
                        />
                      </CCol>
                    </>
                  )}

                  <CCol xs={12}>
                    <CButton
                      color="primary"
                      className="w-100 fw-bold shadow-sm text-white"
                      onClick={handleAddException}
                    >
                      Добавить исключение
                    </CButton>
                  </CCol>
                </CRow>
              </div>
            </div>

            {/* Блок системных функций (для staff) */}
            {isStaff && (
              <div className="settings-section bg-white border">
                <SectionHeader icon={cilSettings} title="Системные функции (Админ)" tooltip={systemTooltip} />
                <CFormCheck
                  id="realtime_stream_enabled"
                  name="realtime_stream_enabled"
                  label={<span className="fw-bold text-dark">Включить Live Трансляцию (Расход трафика!)</span>}
                  checked={schedule.realtime_stream_enabled}
                  onChange={handleRealtimeToggle}
                  className="mb-2"
                />
                <div className="text-muted small mt-1 mb-3">
                  Включайте только для отладки. Планшет будет тратить мобильный интернет на отправку фото.
                </div>

                {!isTruck && (
                  <>
                    <CFormCheck
                      id="save_twin_photos"
                      name="save_twin_photos"
                      label={<span className="fw-bold text-dark d-block">Сохранять скриншоты Цифрового Двойника</span>}
                      checked={schedule.save_twin_photos}
                      onChange={handleTwinPhotosToggle}
                    />
                    <div className="text-muted small mt-1">
                      Отключив эту функцию - объемы и масса продолжат считаться, но картинки в отчете отображаться не будут.
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Блок экспертных настроек ИИ (ТОЛЬКО ДЛЯ СУПЕРЮЗЕРА) */}
            {isSuperuser && (
              <div className="settings-section border-warning border-2">
                <SectionHeader
                  icon={cilSettings}
                  title="Параметры ИИ и BLE (Суперпользователь)"
                  iconClass="text-warning"
                  tooltip="Точная калибровка двухстадийной нейросети BLE и защиты от дублей. Применяется мгновенно при пересчете."
                />
                <CRow className="g-3">
                  <CCol xs={12} md={6}>
                    <FormLabel>Порог детектора BLE (Stage 1)</FormLabel>
                    <CFormInput
                      type="number"
                      step="0.01"
                      min="0.0"
                      max="1.0"
                      size="sm"
                      name="ble_detector_threshold"
                      value={schedule.ble_detector_threshold ?? 0.5}
                      onChange={handleScheduleChange}
                    />
                    <div className="text-muted small mt-1">
                      По умолчанию: <strong>0.50</strong>. Если меньше порога — машина бракуется.
                    </div>
                  </CCol>

                  <CCol xs={12} md={6}>
                    <FormLabel>Анти-дубль кулдаун (сек)</FormLabel>
                    <CFormInput
                      type="number"
                      step="1"
                      min="0"
                      size="sm"
                      name="session_cooldown_sec"
                      value={schedule.session_cooldown_sec ?? 60}
                      onChange={handleScheduleChange}
                    />
                    <div className="text-muted small mt-1">
                      По умолчанию: <strong>60 сек</strong> (COOLDOWN_SECONDS для одной машины).
                    </div>
                  </CCol>
                </CRow>
              </div>
            )}

            {/* КНОПКА СОХРАНЕНИЯ ВСЕХ НАСТРОЕК */}
            <div className="position-sticky bottom-0 bg-light py-3 border-top mt-4 text-center" style={{ zIndex: 10 }}>
              <CButton
                color="primary"
                size="lg"
                className="w-100 fw-bold shadow-sm"
                onClick={handleSaveSchedule}
                disabled={savingSchedule}
              >
                {savingSchedule ? (
                  <CSpinner size="sm" className="me-2" />
                ) : (
                  <CIcon icon={cilSave} className="me-2" />
                )}
                {t("camera.save_schedule")}
              </CButton>
            </div>
          </CTabPane>

          {/* ==========================================
              ВКЛАДКА 2: МАТЕРИАЛЫ
          ========================================== */}
          <CTabPane visible={activeTab === "materials"}>
            <div className="settings-section">
              <SectionHeader icon={cilLayers} title="Справочник плотности грунта" tooltip={t("camera_materials.info_alert")} />

              {materials.length > 0 ? (
                <div className="settings-table-wrapper mb-4 border rounded">
                  <CTable small hover align="middle" className="mb-0">
                    <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <CTableRow>
                        <CTableHeaderCell>{t("camera_materials.name")}</CTableHeaderCell>
                        <CTableHeaderCell>{t("camera_materials.period")}</CTableHeaderCell>
                        <CTableHeaderCell className="text-center">Плотн. (нас/ист)</CTableHeaderCell>
                        <CTableHeaderCell className="text-center">Разупл.</CTableHeaderCell>
                        <CTableHeaderCell className="text-center">К.нап</CTableHeaderCell>
                        <CTableHeaderCell />
                      </CTableRow>
                    </CTableHead>
                    <CTableBody className="bg-white">
                      {materials.map((m) => (
                        <CTableRow key={m.id} color={m.is_default ? "light" : ""}>
                          <CTableDataCell className="fw-bold text-dark">
                            {m.name}{" "}
                            {m.is_default && (
                              <span className="badge bg-secondary ms-1">Default</span>
                            )}
                          </CTableDataCell>
                          <CTableDataCell className="small text-muted">
                            {m.is_default
                              ? "Всегда"
                              : `${dayjs(m.start_date).format("DD.MM.YY")} - ${m.end_date ? dayjs(m.end_date).format("DD.MM.YY") : "..."}`}
                          </CTableDataCell>
                          <CTableDataCell className="small fw-semibold text-center">
                            {m.bulk_density} / {m.true_density}
                          </CTableDataCell>
                          <CTableDataCell className="small text-center">{m.loosening_factor}</CTableDataCell>
                          <CTableDataCell className="small text-center">{m.fill_factor || 1.0}</CTableDataCell>
                          <CTableDataCell className="text-end">
                            <CButton
                              size="sm"
                              color="danger"
                              variant="ghost"
                              onClick={() => handleDeleteMaterial(m.id)}
                            >
                              <CIcon icon={cilTrash} />
                            </CButton>
                          </CTableDataCell>
                        </CTableRow>
                      ))}
                    </CTableBody>
                  </CTable>
                </div>
              ) : (
                <div className="text-center text-muted small p-4 bg-white rounded border border-dashed mb-4">
                  Материалы не заданы. Расчет массы производиться не будет.
                </div>
              )}

              {/* ДОБАВЛЕНИЕ МАТЕРИАЛА */}
              <div className="bg-light p-3 rounded border">
                <h6 className="fw-bold mb-3">{t("camera_materials.add_material")}</h6>

                <CRow className="g-3 mb-4">
                  <CCol xs={12}>
                    <CFormCheck
                      id="is_default_mat"
                      label={<span className="fw-bold">Использовать по умолчанию (без дат)</span>}
                      checked={newMaterial.is_default}
                      onChange={(e) =>
                        setNewMaterial((p) => ({ ...p, is_default: e.target.checked }))
                      }
                    />
                  </CCol>

                  {!newMaterial.is_default && (
                    <CCol xs={12}>
                      <FormLabel>Период действия</FormLabel>
                      <DatePicker
                        selectsRange
                        startDate={newMaterial.startDate}
                        endDate={newMaterial.endDate}
                        onChange={(dates) => {
                          const [start, end] = dates;
                          setNewMaterial((p) => ({ ...p, startDate: start, endDate: end }));
                        }}
                        className="form-control form-control-sm w-100"
                        dateFormat="dd.MM.yyyy"
                        locale="ru"
                      />
                    </CCol>
                  )}

                  <CCol xs={12}>
                    <FormLabel>Название материала</FormLabel>
                    <CFormInput
                      size="sm"
                      type="text"
                      value={newMaterial.name}
                      onChange={(e) => setNewMaterial((p) => ({ ...p, name: e.target.value }))}
                    />
                  </CCol>
                </CRow>

                {/* РАСЧЕТ МАССЫ И ОБЪЕМА */}
                <CRow className="g-3 mb-4">
                  <CCol md={6}>
                    <div className="p-3 bg-white border rounded shadow-sm h-100">
                      <h6 className="text-dark fw-bold mb-3">Расчет массы <br/><span className="text-muted small">(Тоннаж)</span></h6>
                      <FormLabel>Насыпная плотность (кг/м³)</FormLabel>
                      <CFormInput
                        size="sm"
                        type="number"
                        className="mb-3"
                        value={newMaterial.bulk_density}
                        onChange={(e) => {
                          const bd = parseFloat(e.target.value) || 0;
                          const td = newMaterial.true_density;
                          const calculatedKf = bd > 0 ? (td / bd).toFixed(2) : 1;
                          setNewMaterial(p => ({
                            ...p,
                            bulk_density: bd,
                            loosening_factor: parseFloat(calculatedKf)
                          }));
                        }}
                      />
                      <FormLabel>Истинная плотность (кг/м³)</FormLabel>
                      <CFormInput
                        size="sm"
                        type="number"
                        value={newMaterial.true_density}
                        onChange={(e) => {
                          const td = parseFloat(e.target.value) || 0;
                          const bd = newMaterial.bulk_density;
                          const calculatedKf = bd > 0 ? (td / bd).toFixed(2) : 1;
                          setNewMaterial(p => ({
                            ...p,
                            true_density: td,
                            loosening_factor: parseFloat(calculatedKf)
                          }));
                        }}
                      />
                    </div>
                  </CCol>

                  <CCol md={6}>
                    <div className="p-3 bg-white border rounded shadow-sm h-100">
                      <h6 className="text-dark fw-bold mb-3">Расчет объема <br/><span className="text-muted small">(Кубы)</span></h6>
                      <FormLabel>Коэфф. наполнения ковша (К.нап)</FormLabel>
                      <CFormInput
                        size="sm"
                        type="number"
                        step="0.01"
                        className="mb-3"
                        value={newMaterial.fill_factor}
                        onChange={(e) => setNewMaterial((p) => ({ ...p, fill_factor: parseFloat(e.target.value) || 1.0 }))}
                      />
                      <FormLabel>Коэфф. разуплотнения (К.разр)</FormLabel>
                      <CFormInput
                        size="sm"
                        type="number"
                        step="0.01"
                        className="bg-light"
                        value={newMaterial.loosening_factor}
                        onChange={(e) => setNewMaterial((p) => ({ ...p, loosening_factor: parseFloat(e.target.value) || 1.0 }))}
                      />
                    </div>
                  </CCol>
                </CRow>

                <CButton
                  color="primary"
                  size="sm"
                  className="w-100 fw-bold shadow-sm"
                  onClick={handleAddMaterial}
                >
                  {t("camera_materials.save_material", "Сохранить материал")}
                </CButton>
              </div>
            </div>
          </CTabPane>

          {/* ==========================================
              ВКЛАДКА: СПИСОК АДРЕСОВ КП (МУСОРОВОЗ)
          ========================================== */}
          {isGarbage && (
            <CTabPane visible={activeTab === "addresses"}>
              <div className="settings-section p-3">
                <SectionHeader
                  icon={cilMap}
                  title={`Адреса КП (${garbageSites.length})`}
                  tooltip="Если в списке есть хотя бы один адрес, при пересчете аналитики система проверяет все точки списка и подставляет адрес ближайшей площадки в радиусе 50 метров."
                />

                {/* ПАНЕЛЬ ДЕЙСТВИЙ (ВСЕ В МОДАЛЬНЫХ ОКНАХ) */}
                {isStaff && (
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <CButton
                      color="primary"
                      size="sm"
                      className="fw-bold text-white d-flex align-items-center shadow-sm flex-grow-1 justify-content-center"
                      onClick={openAddSiteModal}
                    >
                      <CIcon icon={cilPlus} className="me-1" /> Добавить адрес
                    </CButton>

                    <CButton
                      color="light"
                      size="sm"
                      className="fw-semibold border text-dark d-flex align-items-center shadow-sm flex-grow-1 justify-content-center"
                      onClick={() => setImportModalVisible(true)}
                    >
                      <CIcon icon={cilCloudUpload} className="me-1 text-primary" /> Импорт Excel
                    </CButton>

                    {garbageSites.length > 0 && (
                      <CButton
                        color="danger"
                        variant="ghost"
                        size="sm"
                        className="d-flex align-items-center px-2"
                        title="Очистить весь список"
                        onClick={() =>
                          setDeleteConfirmModal({
                            visible: true,
                            type: "all",
                            site: null,
                            deleting: false,
                          })
                        }
                      >
                        <CIcon icon={cilTrash} />
                      </CButton>
                    )}
                  </div>
                )}

                {/* ПОИСК ПО АДРЕСАМ */}
                {garbageSites.length > 0 && (
                  <CInputGroup size="sm" className="mb-3 shadow-sm">
                    <CInputGroupText className="bg-white border-end-0 text-muted">
                      <CIcon icon={cilSearch} size="sm" />
                    </CInputGroupText>
                    <CFormInput
                      className="border-start-0 ps-0"
                      placeholder="Поиск по адресу, ID или координатам..."
                      value={siteSearchQuery}
                      onChange={(e) => setSiteSearchQuery(e.target.value)}
                    />
                    {siteSearchQuery && (
                      <CButton
                        color="light"
                        variant="ghost"
                        size="sm"
                        onClick={() => setSiteSearchQuery("")}
                      >
                        <CIcon icon={cilX} size="sm" />
                      </CButton>
                    )}
                  </CInputGroup>
                )}

                {/* КОМПАКТНЫЙ СПИСОК КАРТОЧЕК БЕЗ ГОРИЗОНТАЛЬНОГО СКРОЛЛА */}
                {loadingSites ? (
                  <div className="text-center p-5">
                    <CSpinner size="sm" color="primary" />
                  </div>
                ) : filteredGarbageSites.length > 0 ? (
                  <div
                    className="border rounded bg-white"
                    style={{ maxHeight: "calc(100vh - 310px)", minHeight: "300px", overflowY: "auto" }}
                  >
                    {filteredGarbageSites.map((site, idx) => (
                      <div
                        key={site.id}
                        className={`site-list-item p-3 d-flex justify-content-between align-items-start gap-2 ${
                          idx !== filteredGarbageSites.length - 1 ? "border-bottom" : ""
                        }`}
                      >
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            className="fw-bold text-dark mb-1"
                            style={{ fontSize: "0.9rem", lineHeight: "1.35", wordBreak: "break-word" }}
                          >
                            {site.address}
                          </div>
                          <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                            {site.external_id && (
                              <span
                                className="badge bg-light text-dark border font-monospace"
                                style={{ fontSize: "0.75rem" }}
                              >
                                ID: {site.external_id}
                              </span>
                            )}
                            <span
                              className="text-muted font-monospace d-inline-flex align-items-center"
                              style={{ fontSize: "0.75rem" }}
                            >
                              <CIcon icon={cilLocationPin} size="sm" className="me-1 text-secondary" />
                              {Number(site.latitude).toFixed(6)}, {Number(site.longitude).toFixed(6)}
                            </span>
                          </div>
                        </div>

                        {isStaff && (
                          <div className="d-flex align-items-center gap-1 flex-shrink-0">
                            <CButton
                              size="sm"
                              color="light"
                              className="border p-1 px-2 text-primary"
                              title="Редактировать"
                              onClick={() => openEditSiteModal(site)}
                            >
                              <CIcon icon={cilPencil} size="sm" />
                            </CButton>
                            <CButton
                              size="sm"
                              color="light"
                              className="border p-1 px-2 text-danger"
                              title="Удалить"
                              onClick={() =>
                                setDeleteConfirmModal({
                                  visible: true,
                                  type: "single",
                                  site,
                                  deleting: false,
                                })
                              }
                            >
                              <CIcon icon={cilTrash} size="sm" />
                            </CButton>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : garbageSites.length > 0 ? (
                  <div className="text-center text-muted small p-4 bg-white rounded border">
                    По запросу «{siteSearchQuery}» ничего не найдено.
                  </div>
                ) : (
                  <div className="text-center text-muted small p-5 bg-white rounded border border-dashed">
                    <CIcon icon={cilMap} size="xl" className="mb-2 opacity-50" />
                    <div className="fw-semibold text-dark mb-1">Список адресов пуст</div>
                    <div>Нажмите «Добавить адрес» или загрузите готовый список через «Импорт Excel».</div>
                  </div>
                )}
              </div>
            </CTabPane>
          )}

          {/* ==========================================
              ВКЛАДКА 3: КАРТА
          ========================================== */}
          {!isTruck && (
            <CTabPane visible={activeTab === "map"}>
              <div className="settings-section p-0 overflow-hidden shadow-sm position-relative">
                <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 100 }}>
                  <CButton color="dark" size="sm" onClick={() => setFullScreenMapVisible(true)} className="shadow">
                    <CIcon icon={cilFullscreen} className="me-1" /> На весь экран
                  </CButton>
                </div>
                <div style={{ height: "350px", width: "100%" }}>
                   {sites.length > 0 ? renderGarbageMap(false) : <GPSMap trajectory={trajectoryData} currentTime={currentAbsoluteTime} height="350px" />}
                </div>
              </div>
              <div className="text-muted small text-center mt-2">
                <CIcon icon={cilInfo} className="me-1" /> {t("camera_map.hybrid_mode_hint")}
              </div>
            </CTabPane>
          )}

          {/* ==========================================
              ВКЛАДКА 4: ТЕЛЕМЕТРИЯ
          ========================================== */}
          <CTabPane visible={activeTab === "telemetry"}>
            <div className="settings-section border-0 shadow-sm">
              <SectionHeader icon={cilChartLine} title="Телеметрия с аппаратных датчиков" />
              {!telemetryData ? (
                <div className="text-center p-5 text-muted bg-light rounded border border-dashed">
                  {t("camera_telemetry.no_data")}
                </div>
              ) : (
                <>
                  <CRow className="mb-4 g-2 g-md-3">
                    {/* 1. Карточка процессора */}
                    <CCol xs={6} md={3}>
                      <div className="bg-light rounded p-2 p-md-3 text-center border h-100 d-flex flex-column justify-content-center align-items-center">
                        <div className="fw-bold text-muted small mb-1 d-flex align-items-center justify-content-center gap-1" style={{ fontSize: "0.75rem", lineHeight: "1.2" }}>
                          <CIcon icon={cilSpeedometer} size="sm" />
                          <span>CPU (Ядра)</span>
                        </div>
                        <h4 className="text-danger fw-bold mb-0" style={{ fontSize: "1.25rem" }}>
                          {telemetryData.summary.avg_cpu_real > 0 
                            ? `${Math.round(telemetryData.summary.avg_cpu_real)}°C` 
                            : "—"}
                        </h4>
                        <span className="text-muted mt-1" style={{ fontSize: "0.75rem", lineHeight: "1.2" }}>
                          АКБ: {telemetryData.summary.avg_cpu_temp ? `${telemetryData.summary.avg_cpu_temp}°C` : "—"}
                        </span>
                      </div>
                    </CCol>

                    {/* 2. Карточка аккумулятора */}
                    <CCol xs={6} md={3}>
                      <div className="bg-light rounded p-2 p-md-3 text-center border h-100 d-flex flex-column justify-content-center align-items-center">
                        <div className="fw-bold text-muted small mb-1 d-flex align-items-center justify-content-center gap-1" style={{ fontSize: "0.75rem", lineHeight: "1.2" }}>
                          <CIcon icon={cilBatteryFull} size="sm" />
                          <span>Заряд АКБ</span>
                        </div>
                        <h4 className="text-success fw-bold mb-0" style={{ fontSize: "1.25rem" }}>
                          {telemetryData.summary.avg_battery || 0}%
                        </h4>
                        <span className="mt-1" style={{ fontSize: "0.75rem", lineHeight: "1.2", color: telemetryData.summary.latest_voltage > 0 ? "#0d6efd" : "#6c757d", fontWeight: "600" }}>
                          {telemetryData.summary.latest_voltage > 0 
                            ? `⚡ ${Number(telemetryData.summary.latest_voltage).toFixed(2)} В` 
                            : "Вольтаж: —"}
                        </span>
                      </div>
                    </CCol>

                    {/* 3. Карточка памяти/диска */}
                    <CCol xs={6} md={3}>
                      <div className="bg-light rounded p-2 p-md-3 text-center border h-100 d-flex flex-column justify-content-center align-items-center">
                        <div className="fw-bold text-muted small mb-1 d-flex align-items-center justify-content-center gap-1" style={{ fontSize: "0.75rem", lineHeight: "1.2" }}>
                          <CIcon icon={cibLaravel} size="sm" />
                          <span>Диск</span>
                        </div>
                        <h4 className="text-primary fw-bold mb-0" style={{ fontSize: "1.25rem" }}>
                          {telemetryData.summary.hdd_usage || 0}%
                        </h4>
                      </div>
                    </CCol>

                    {/* 4. Карточка сигнала связи */}
                    <CCol xs={6} md={3}>
                      <div className="bg-light rounded p-2 p-md-3 text-center border h-100 d-flex flex-column justify-content-center align-items-center">
                        <div className="fw-bold text-muted small mb-1 d-flex align-items-center justify-content-center gap-1" style={{ fontSize: "0.75rem", lineHeight: "1.2" }}>
                          <CIcon icon={cilMobile} size="sm" />
                          <span>Сигнал</span>
                        </div>
                        <h4 className="text-info fw-bold mb-0" style={{ fontSize: "1.25rem" }}>
                          {telemetryData.summary.avg_signal || 0}/4
                        </h4>
                      </div>
                    </CCol>
                  </CRow>

                  {/* 1. ГРАФИК ТЕМПЕРАТУРЫ И ЗАРЯДА БАТАРЕИ */}
                  <h6 className="fw-bold mb-2">{t("camera_telemetry.temp_and_batt")}</h6>
                  <div style={{ height: "230px" }} className="mb-4">
                    <ResponsiveLine
                      data={[...(telemetryData.temp || []), ...(telemetryData.battery || [])]}
                      margin={{ top: 10, right: 10, bottom: 40, left: 40 }}
                      xScale={{
                        type: "time",
                        format: "native",
                        precision: "minute",
                      }}
                      xFormat="time:%H:%M"
                      yScale={{
                        type: "linear",
                        min: 0,
                        max: 100,
                        stacked: false,
                        reverse: false,
                      }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        format: "%H:%M",
                        tickValues: 5,
                        legend: "Время",
                        legendOffset: 32,
                        legendPosition: "middle",
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: "Значение",
                        legendOffset: -35,
                        legendPosition: "middle",
                      }}
                      pointSize={3}
                      pointColor={{ theme: "background" }}
                      pointBorderWidth={2}
                      pointBorderColor={{ from: "serieColor" }}
                      useMesh
                      enableSlices="x"
                    />
                  </div>

                  {/* 2. ГРАФИК НАПРЯЖЕНИЯ АККУМУЛЯТОРА */}
                  <h6 className="fw-bold mb-2">Напряжение аккумулятора (В)</h6>
                  <div style={{ height: "160px" }} className="mb-4">
                    <ResponsiveLine
                      data={telemetryData.voltage || []}
                      margin={{ top: 10, right: 10, bottom: 40, left: 40 }}
                      xScale={{
                        type: "time",
                        format: "native",
                        precision: "minute",
                      }}
                      xFormat="time:%H:%M"
                      yScale={{ 
                        type: "linear", 
                        min: 0, 
                        max: 5,
                        stacked: false 
                      }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{ format: "%H:%M", tickValues: 5 }}
                      axisLeft={{ 
                        tickSize: 5, 
                        tickPadding: 5, 
                        legend: "Вольт (В)", 
                        legendOffset: -35, 
                        legendPosition: "middle" 
                      }}
                      pointSize={3}
                      pointColor={{ theme: "background" }}
                      pointBorderWidth={2}
                      pointBorderColor={{ from: "serieColor" }}
                      useMesh
                      enableSlices="x"
                      colors={["#9c27b0"]}
                    />
                  </div>

                  {/* 3. ГРАФИК УРОВНЯ СИГНАЛА */}
                  <h6 className="fw-bold mb-2">{t("camera_telemetry.signal_level")}</h6>
                  <div style={{ height: "140px" }} className="mb-2">
                    <ResponsiveLine
                      data={telemetryData.signal || []}
                      margin={{ top: 10, right: 10, bottom: 35, left: 40 }}
                      xScale={{
                        type: "time",
                        format: "native",
                        precision: "minute",
                      }}
                      xFormat="time:%H:%M"
                      yScale={{ type: "linear", min: 0, max: 4, stacked: false }}
                      curve="stepAfter"
                      axisBottom={{ format: "%H:%M", tickValues: 5 }}
                      axisLeft={{ tickValues: [0, 1, 2, 3, 4] }}
                      enablePoints={false}
                      useMesh
                      enableSlices="x"
                      colors={["#6610f2"]}
                    />
                  </div>
                </>
              )}
            </div>
          </CTabPane>

          {/* ==========================================
              ВКЛАДКА: ТОПЛИВО (СИМУЛЯЦИЯ)
          ========================================== */}
          <CTabPane visible={activeTab === "fuel"}>
            <div className="settings-section border-0 shadow-sm">
              <SectionHeader icon={cilSpeedometer} title="Симуляция датчика уровня топлива" iconClass="text-warning" />

              <div className="bg-light p-3 rounded border mb-4">
                <CRow className="g-3 align-items-end">
                  <CCol xs={12} md={6}>
                  <FormLabel>Объем бака {isTruck ? "самосвала" : "экскаватора"} (литры)</FormLabel>
                    <CFormInput
                      type="number"
                      size="sm"
                      name="fuel_tank_volume"
                      value={schedule.fuel_tank_volume}
                      onChange={handleScheduleChange}
                      className="fw-bold text-primary"
                    />
                  </CCol>
                  <CCol xs={12} md={6}>
                    <CButton color="primary" size="sm" className="w-100 fw-bold shadow-sm text-white" onClick={handleSaveSchedule} disabled={savingSchedule}>
                      {savingSchedule ? <CSpinner size="sm" /> : "Сохранить объем бака"}
                    </CButton>
                  </CCol>
                </CRow>
                <div className="text-muted small mt-2">
                  Для работы расчета убедитесь, что в главных Настройках техники выбрана "Спецификация техники (Топливо)".
                </div>
              </div>

              {!analyticsData?.virtual_fuel ? (
                <div className="text-center p-5 text-muted bg-light rounded border border-dashed">
                  Спецификация не задана или нет данных за выбранный период.
                </div>
              ) : (
                <CRow className="g-4 align-items-center">
                  <CCol xs={12} md={5} className="d-flex justify-content-center">
                    <div style={{ width: "140px", height: "300px", border: "6px solid #495057", borderRadius: "16px", position: "relative", backgroundColor: "#fff", overflow: "hidden", boxShadow: "inset 0 4px 10px rgba(0,0,0,0.1), 0 8px 20px rgba(0,0,0,0.1)" }}>
                      <div style={{ position: "absolute", top: "25%", left: 0, width: "15px", height: "2px", backgroundColor: "rgba(0,0,0,0.3)", zIndex: 5 }} />
                      <div style={{ position: "absolute", top: "50%", left: 0, width: "25px", height: "3px", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 5 }} />
                      <div style={{ position: "absolute", top: "75%", left: 0, width: "15px", height: "2px", backgroundColor: "rgba(0,0,0,0.3)", zIndex: 5 }} />

                      <div style={{
                        position: "absolute", bottom: 0, left: 0, width: "100%", height: `${analyticsData.virtual_fuel.remaining_pct}%`,
                        background: analyticsData.virtual_fuel.remaining_pct > 20 ? "linear-gradient(to top, #f9b115, #fcd066)" : "linear-gradient(to top, #e55353, #f86c6b)",
                        transition: "height 1.5s cubic-bezier(0.25, 0.8, 0.25, 1)", boxShadow: "0 -4px 10px rgba(0,0,0,0.2)"
                      }}>
                        <div style={{ position: "absolute", top: "-5px", left: 0, right: 0, height: "10px", background: "rgba(255,255,255,0.2)", borderRadius: "50%" }} />
                      </div>

                      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontSize: "1.5rem", fontWeight: "900", color: "#333", zIndex: 10, textShadow: "0 0 10px rgba(255,255,255,0.8)" }}>
                        {analyticsData.virtual_fuel.remaining_pct}%
                      </div>
                    </div>
                  </CCol>

                  <CCol xs={12} md={7}>
                    <div className="d-flex flex-column gap-3">
                      <div className="p-3 bg-light border rounded shadow-sm">
                        <div className="text-muted small fw-bold text-uppercase">Остаток в баке (Расчетный)</div>
                        <h2 className={`fw-bold mb-0 ${analyticsData.virtual_fuel.remaining_pct > 20 ? 'text-success' : 'text-danger'}`}>
                          {analyticsData.virtual_fuel.remaining_liters} л
                        </h2>
                        <div className="small text-muted mt-1">Из {analyticsData.virtual_fuel.tank_volume} литров общей вместимости</div>
                      </div>

                      <div className="p-3 bg-white border border-danger border-opacity-50 rounded shadow-sm">
                        <div className="text-muted small fw-bold text-uppercase">Расход за период</div>
                        <h3 className="fw-bold text-danger mb-2">-{analyticsData.virtual_fuel.total_liters} л</h3>

                        <div className="d-flex flex-column gap-2 mt-3 pt-2 border-top">
                          <div className="d-flex justify-content-between small">
                            <span className="text-muted">Холостой ход (простои):</span>
                            <strong className="text-dark">{analyticsData.virtual_fuel.liters_idle} л</strong>
                          </div>
                          <div className="d-flex justify-content-between small">
                            <span className="text-muted">
                              {isTruck ? "Средняя нагрузка (перемещение пустой, погрузка, разгрузка):" : "Средняя нагрузка (копание):"}
                            </span>
                            <strong className="text-dark">{analyticsData.virtual_fuel.liters_medium} л</strong>
                          </div>
                          <div className="d-flex justify-content-between small">
                            <span className="text-muted">
                              {isTruck ? "Высокая нагрузка (перемещение груженый):" : "Высокая нагрузка (поворот с грузом):"}
                            </span>
                            <strong className="text-dark">{analyticsData.virtual_fuel.liters_high} л</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CCol>
                </CRow>
              )}
            </div>
          </CTabPane>

          {/* ==========================================
              ВКЛАДКА 5: АЛЕРТЫ
          ========================================== */}
          <CTabPane visible={activeTab === "alerts"}>
            <div className="settings-section border-0 shadow-sm">
              <SectionHeader icon={cilBell} title={t("alerts.title", "Инциденты")} iconClass="text-danger" />
              <div className="text-muted small d-block d-md-none mb-2 text-center">← Свайпните таблицу влево-вправо →</div>
              <DeviceAlertsTab cameraId={cameraId} />
            </div>
          </CTabPane>

          {/* ==========================================
              ВКЛАДКА 6: ПРОСТОИ
          ========================================== */}
          <CTabPane visible={activeTab === "downtimes"}>
            <div className="settings-section border-0 shadow-sm">
              <DowntimesTab
                deviceId={cameraId}
                startDate={analyticsData?.date_range?.start ? new Date(analyticsData.date_range.start) : null}
                endDate={analyticsData?.date_range?.end ? new Date(analyticsData.date_range.end) : null}
                isStaff={isStaff}
                autoIdleEvents={isTruck ? analyticsData?.idle_events : null}
              />
            </div>
          </CTabPane>

          {/* ==========================================
              ВКЛАДКА 7: ЦИФРОВОЙ ДВОЙНИК (AI)
          ========================================== */}
          {analyticsData?.use_digital_twin && (
            <CTabPane visible={activeTab === "twin"}>
              <div className="settings-section border-0 shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                  <div>
                    <h5 className="mb-1 text-primary fw-bold">Анализ ковша нейросетью</h5>
                    <span className="text-muted small">Depth Estimation & 3D Physics Simulation</span>
                  </div>
                  <CButton size="sm" color="primary" variant="outline" onClick={handleRecalculateTwin} disabled={twinRecalculating} className="fw-bold shadow-sm">
                    {twinRecalculating ? <CSpinner size="sm" /> : <><CIcon icon={cilReload} className="me-1" /> Пересчитать</>}
                  </CButton>
                </div>

                {totalTwinItems > 0 ? (
                  <div ref={twinScrollRef} style={{ maxHeight: "600px", overflowY: "auto", paddingRight: "5px" }}>
                    {currentTwinData.map((twin, idx) => (
                      <div key={idx} className="bg-light border rounded mb-3 overflow-hidden d-flex flex-column flex-md-row shadow-sm">
                        <div className="p-2 bg-dark d-flex align-items-center justify-content-center" style={{ width: "100%", minHeight: "180px" }}>
                          {twin.image_url ? (
                            <img src={twin.image_url} alt="Alignment" loading="lazy" style={{ width: "100%", height: "100%", maxHeight: "250px", objectFit: "contain", cursor: "pointer" }} onClick={() => { if (twin.image_url) { setViewerUrl(twin.image_url); setViewerModalVisible(true); } }} />
                          ) : (
                            <div className="text-muted small">{t("camera_twin.no_photo")}</div>
                          )}
                        </div>

                        <div className="p-3 flex-grow-1 bg-white">
                          <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                            <strong className="text-primary fs-5">{dayjs(twin.timestamp).format("DD.MM.YY HH:mm:ss")}</strong>
                            <CButton size="sm" color="primary" onClick={() => { if (twin.image_url) { setViewerUrl(twin.image_url); setViewerModalVisible(true); } }}>
                              <CIcon icon={cilInfo} className="me-1" /> Просмотр фото
                            </CButton>
                          </div>

                          <CRow className="text-muted mb-2 g-2 small">
                            <CCol xs={6}>Высота: <strong className="text-dark">+{twin.soil_height.toFixed(2)} м</strong></CCol>
                            <CCol xs={6}>Шапка: <strong className="text-dark">{twin.cap_volume.toFixed(2)} м³</strong></CCol>
                            <CCol xs={6}>Объем внутри: <strong className="text-dark">{twin.inner_volume.toFixed(2)} м³</strong></CCol>
                            <CCol xs={6}>К.разр: <strong className="text-dark">{twin.loosening_factor}</strong></CCol>
                          </CRow>

                          <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top bg-light p-2 rounded">
                            <span className="text-success fw-bold fs-5">{twin.total_volume.toFixed(2)} м³</span>
                            <span className="text-info fw-bold fs-5">{(twin.mass / 1000).toFixed(2)} т</span>
                          </div>
                        </div>
                      </div>
                    ))}

                    {totalTwinPages > 1 && (
                      <CPagination align="center" className="mt-4" size="sm">
                        <CPaginationItem disabled={twinPage === 1} onClick={() => setTwinPage(twinPage - 1)} style={{ cursor: twinPage === 1 ? "default" : "pointer" }}>
                          {t("camera_twin.prev")}
                        </CPaginationItem>
                        {getPaginationItems(twinPage, totalTwinPages).map((item, idx) => (
                          <CPaginationItem key={idx} active={item === twinPage} disabled={item === "..."} onClick={() => item !== "..." && setTwinPage(item)} style={{ cursor: item === "..." ? "default" : "pointer" }}>
                            {item}
                          </CPaginationItem>
                        ))}
                        <CPaginationItem disabled={twinPage === totalTwinPages} onClick={() => setTwinPage(twinPage + 1)} style={{ cursor: twinPage === totalTwinPages ? "default" : "pointer" }}>
                          {t("camera_twin.next")}
                        </CPaginationItem>
                      </CPagination>
                    )}
                  </div>
                ) : (
                  <div className="text-center p-5 text-muted bg-light rounded border border-dashed">
                    <CIcon icon={cibLaravel} size="3xl" className="mb-3 opacity-25" />
                    <p>Нет расчетов за этот период.</p>
                  </div>
                )}
              </div>
            </CTabPane>
          )}

          {/* ==========================================
              ВКЛАДКА 8: LIVE
          ========================================== */}
          <CTabPane visible={activeTab === "live"}>
            <div className="settings-section border-0 shadow-sm p-4">
              {!schedule.realtime_stream_enabled ? (
                <div className="text-center text-muted py-5 border rounded bg-light border-dashed">
                  <CIcon icon={cilVideo} size="3xl" className="mb-3 opacity-25" />
                  <h5 className="text-dark fw-bold">Live-режим отключен</h5>
                  <p className="mb-0">Администратор должен включить трансляцию в Настройках.</p>
                </div>
              ) : !liveData ? (
                <div className="text-center py-5">
                  <CSpinner color="danger" /> <p className="mt-3 text-muted fw-bold">Ожидание данных от планшета...</p>
                </div>
              ) : (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                    <h5 className="text-danger fw-bold mb-0 d-flex align-items-center">
                      <span className="live-dot-fixed" /> Трансляция активна
                    </h5>
                    <span className="text-muted small fw-bold bg-light px-2 py-1 rounded">
                      Обновлено: {liveData.timestamp ? dayjs(liveData.timestamp).format("HH:mm:ss") : "Нет связи"}
                    </span>
                  </div>

                  <div className="bg-black rounded overflow-hidden mb-4 shadow-lg position-relative" style={{ height: "400px" }} onClick={() => setLiveImageModalVisible(true)}>
                    {liveData.frame_url ? (
                      <img src={`${liveData.frame_url}?t=${Date.now()}`} alt="Live stream" style={{ width: "100%", height: "100%", objectFit: "contain", cursor: "pointer", transition: "opacity 0.2s" }} onMouseEnter={(e) => { e.target.style.opacity = "0.8"; }} onMouseLeave={(e) => { e.target.style.opacity = "1"; }} aria-hidden="true" />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center text-white h-100">Нет изображения</div>
                    )}
                    <div className="position-absolute bottom-0 end-0 p-2 text-white small" style={{ background: "rgba(0,0,0,0.5)", borderTopLeftRadius: "8px", pointerEvents: "none" }}>
                      Live Feed (5s)
                    </div>
                  </div>

                  <CRow className="g-3 mb-4">
                    <CCol xs={4}>
                      <div className="bg-white border rounded p-3 text-center shadow-sm h-100 d-flex flex-column justify-content-center align-items-center">
                        <FormLabel className="text-truncate w-100 text-center">CPU (Ядра)</FormLabel>
                        <h4 className="text-danger fw-bold mb-0">
                          {liveData.telemetry?.cpu_temp_real ? `${Math.round(liveData.telemetry.cpu_temp_real)}°C` : `${liveData.telemetry?.cpu_temp || 0}°C`}
                        </h4>
                        {liveData.telemetry?.cpu_temp ? (
                          <span className="small text-muted" style={{ fontSize: "0.75rem" }}>
                            АКБ: {liveData.telemetry.cpu_temp}°C
                          </span>
                        ) : null}
                      </div>
                    </CCol>

                    <CCol xs={4}>
                      <div className="bg-white border rounded p-3 text-center shadow-sm h-100 d-flex flex-column justify-content-center align-items-center">
                        <FormLabel className="text-truncate w-100 text-center">
                          Заряд АКБ {liveData.telemetry?.is_charging ? "⚡" : ""}
                        </FormLabel>
                        <h4 className="text-success fw-bold mb-0">{liveData.telemetry?.battery_pct || 0}%</h4>
                        {liveData.telemetry?.battery_voltage > 0 ? (
                          <span className="small text-primary fw-bold" style={{ fontSize: "0.75rem" }}>
                            ⚡ {liveData.telemetry.battery_voltage.toFixed(2)} В
                          </span>
                        ) : null}
                      </div>
                    </CCol>

                    <CCol xs={4}>
                      <div className="bg-white border rounded p-3 text-center shadow-sm h-100 d-flex flex-column justify-content-center align-items-center">
                        <FormLabel className="text-truncate w-100 text-center">Сигнал связи</FormLabel>
                        <h4 className="text-primary fw-bold mb-0">{liveData.telemetry?.network_signal || 0}/4</h4>
                      </div>
                    </CCol>
                  </CRow>

                  <CCard className="border-0 shadow-sm mt-4">
                    <CCardHeader className="bg-white fw-bold d-flex align-items-center">
                      <CIcon icon={cilMap} className="text-primary me-2" /> Текущее местоположение (GPS)
                    </CCardHeader>
                    <CCardBody className="p-0">
                      {liveData.lat > 0 && liveData.lon > 0 ? (
                        <div style={{ height: "300px" }}>
                          <GPSMap trajectory={[{ lat: liveData.lat, lon: liveData.lon, ts: liveData.timestamp }]} currentTime={liveData.timestamp} height="300px" />
                        </div>
                      ) : (
                        <div className="p-4 text-center text-muted bg-light border-bottom rounded-bottom">
                          GPS координаты не получены
                        </div>
                      )}
                    </CCardBody>
                  </CCard>
                </div>
              )}
            </div>
          </CTabPane>
        </CTabContent>
      </div>

      {/* =================================================================
          МОДАЛЬНОЕ ОКНО 1: ДОБАВЛЕНИЕ / РЕДАКТИРОВАНИЕ АДРЕСА КП
      ================================================================= */}
      <CModal
        visible={siteFormModal.visible}
        onClose={() => !siteFormModal.saving && setSiteFormModal((p) => ({ ...p, visible: false }))}
        alignment="center"
      >
        <CModalHeader closeButton>
          <CModalTitle className="fw-bold">
            {siteFormModal.mode === "add" ? "Добавить площадку КП" : "Редактировать площадку КП"}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div className="mb-3">
            <FormLabel>Адрес площадки *</FormLabel>
            <CFormInput
              placeholder="Например: г Рязань, ул Зубковой, д 26"
              value={siteFormModal.address}
              onChange={(e) => setSiteFormModal((p) => ({ ...p, address: e.target.value }))}
            />
          </div>

          <div className="mb-3">
            <FormLabel>Идентификатор (необязательно)</FormLabel>
            <CFormInput
              placeholder="Например: 7010300535"
              value={siteFormModal.external_id}
              onChange={(e) => setSiteFormModal((p) => ({ ...p, external_id: e.target.value }))}
            />
          </div>

          <CRow className="g-3">
            <CCol xs={12} sm={6}>
              <FormLabel>Широта (Latitude) *</FormLabel>
              <CFormInput
                type="number"
                step="any"
                placeholder="54.601612"
                value={siteFormModal.latitude}
                onChange={(e) => setSiteFormModal((p) => ({ ...p, latitude: e.target.value }))}
              />
            </CCol>
            <CCol xs={12} sm={6}>
              <FormLabel>Долгота (Longitude) *</FormLabel>
              <CFormInput
                type="number"
                step="any"
                placeholder="39.813645"
                value={siteFormModal.longitude}
                onChange={(e) => setSiteFormModal((p) => ({ ...p, longitude: e.target.value }))}
              />
            </CCol>
          </CRow>
        </CModalBody>
        <CModalFooter className="bg-light">
          <CButton
            color="secondary"
            variant="ghost"
            onClick={() => setSiteFormModal((p) => ({ ...p, visible: false }))}
            disabled={siteFormModal.saving}
          >
            Отмена
          </CButton>
          <CButton
            color="primary"
            className="fw-bold text-white px-4"
            onClick={handleSaveSiteModal}
            disabled={siteFormModal.saving}
          >
            {siteFormModal.saving ? (
              <CSpinner size="sm" className="me-2" />
            ) : (
              <CIcon icon={cilSave} className="me-2" />
            )}
            Сохранить
          </CButton>
        </CModalFooter>
      </CModal>

      {/* =================================================================
          МОДАЛЬНОЕ ОКНО 2: ИМПОРТ СПИСКА АДРЕСОВ ИЗ EXCEL
      ================================================================= */}
      <CModal
        visible={importModalVisible}
        onClose={() => !uploadingExcel && setImportModalVisible(false)}
        alignment="center"
      >
        <CModalHeader closeButton>
          <CModalTitle className="fw-bold">Импорт адресов КП из Excel</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDraggingExcel(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDraggingExcel(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setIsDraggingExcel(false);
              const file = e.dataTransfer.files?.[0];
              if (file) handleExcelFileUpload(file);
            }}
            onClick={() => !uploadingExcel && excelInputRef.current?.click()}
            className="p-4 text-center rounded border mb-3"
            style={{
              borderStyle: "dashed",
              borderWidth: "2px",
              borderColor: isDraggingExcel ? "#321fdb" : "#adb5bd",
              backgroundColor: isDraggingExcel ? "rgba(50, 31, 219, 0.06)" : "#f8f9fa",
              cursor: uploadingExcel ? "default" : "pointer",
              transition: "all 0.2s ease",
            }}
          >
            <input
              ref={excelInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={(e) => handleExcelFileUpload(e.target.files?.[0])}
            />
            {uploadingExcel ? (
              <div className="py-3">
                <CSpinner size="sm" color="primary" className="me-2" />
                <span className="fw-bold text-primary">Загрузка и обработка файла...</span>
              </div>
            ) : (
              <div className="py-2">
                <CIcon icon={cilCloudUpload} size="xxl" className="text-primary mb-2" />
                <div className="fw-bold text-dark mb-1">
                  Перетащите Excel-файл (.xlsx) сюда или нажмите для выбора
                </div>
                <div className="text-muted small">
                  Ожидаемые колонки: <b>Адрес</b> | <b>Идентификатор</b> | <b>Широта</b> | <b>Долгота</b>
                </div>
              </div>
            )}
          </div>

          <CFormCheck
            id="replaceExcelSitesModal"
            label={<span className="fw-semibold text-dark">Полностью заменить текущий список при импорте</span>}
            checked={replaceOnImport}
            onChange={(e) => setReplaceOnImport(e.target.checked)}
          />
          <div className="text-muted small ms-4">
            Если галочка не стоит, новые адреса из файла будут добавлены к уже существующим.
          </div>
        </CModalBody>
        <CModalFooter className="bg-light">
          <CButton
            color="secondary"
            variant="ghost"
            onClick={() => setImportModalVisible(false)}
            disabled={uploadingExcel}
          >
            Закрыть
          </CButton>
        </CModalFooter>
      </CModal>

      {/* =================================================================
          МОДАЛЬНОЕ ОКНО 3: ПОДТВЕРЖДЕНИЕ УДАЛЕНИЯ
      ================================================================= */}
      <CModal
        visible={deleteConfirmModal.visible}
        onClose={() =>
          !deleteConfirmModal.deleting &&
          setDeleteConfirmModal({ visible: false, type: "single", site: null, deleting: false })
        }
        alignment="center"
      >
        <CModalHeader closeButton>
          <CModalTitle className="fw-bold text-danger">Подтверждение удаления</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {deleteConfirmModal.type === "all" ? (
            <div>
              Вы действительно хотите удалить <strong>все адреса ({garbageSites.length} шт.)</strong> для этого мусоровоза? Это действие нельзя отменить.
            </div>
          ) : (
            <div>
              Вы действительно хотите удалить площадку по адресу:
              <div className="p-2 bg-light border rounded mt-2 fw-bold text-dark">
                {deleteConfirmModal.site?.address}
              </div>
            </div>
          )}
        </CModalBody>
        <CModalFooter className="bg-light">
          <CButton
            color="secondary"
            variant="ghost"
            disabled={deleteConfirmModal.deleting}
            onClick={() =>
              setDeleteConfirmModal({ visible: false, type: "single", site: null, deleting: false })
            }
          >
            Отмена
          </CButton>
          <CButton
            color="danger"
            className="fw-bold text-white px-4"
            disabled={deleteConfirmModal.deleting}
            onClick={handleConfirmDelete}
          >
            {deleteConfirmModal.deleting ? (
              <CSpinner size="sm" className="me-2" />
            ) : (
              <CIcon icon={cilTrash} className="me-2" />
            )}
            Удалить
          </CButton>
        </CModalFooter>
      </CModal>

      {/* МОДАЛЬНОЕ ОКНО КАРТЫ (ПОЛНОЭКРАННОЕ) */}
      <CModal visible={fullScreenMapVisible} onClose={() => setFullScreenMapVisible(false)} size="xl" fullscreen>
        <CModalHeader closeButton className="bg-light">
          <CModalTitle className="fw-bold"><CIcon icon={cilMap} className="me-2 text-primary" />Карта маршрута и остановок</CModalTitle>
        </CModalHeader>
        <CModalBody className="p-0 bg-light">
          <div style={{ height: "100%", width: "100%", minHeight: "80vh" }}>
            {sites.length > 0 ? renderGarbageMap(true) : <GPSMap trajectory={trajectoryData} currentTime={currentAbsoluteTime} height="100%" />}
          </div>
        </CModalBody>
      </CModal>
    </div>
  );
};

export default CameraTabs;